/**
 * Render interpolation between the last two sim states (AD-007a). The sim
 * runs at a fixed 60 Hz; a display running at any other rate — or a frame
 * that lands mid-tick — needs a pose *between* two states, not the most
 * recent one, or the aircraft judders. Interpolation is part of the engine's
 * contract, not render glue: it belongs next to the state it interpolates
 * rather than reimplemented per consumer.
 *
 * Operates on the engine's plain `Vec3`/`Quat` records rather than three.js
 * objects, so it stays testable without a GPU and the result can be copied
 * straight into `Object3D.position`/`.quaternion` (the engine's frame is
 * three.js's: right-handed, Y up, forward −Z).
 */

import { lerpVec3, slerpQuat, type Quat, type Vec3 } from "./math3d.ts";
import type { SimState } from "./state.ts";

export interface Pose {
  readonly position: Vec3;
  readonly orientation: Quat;
}

export function interpolatePose(previous: Pose, next: Pose, alpha: number): Pose {
  const t = Math.min(Math.max(alpha, 0), 1);
  return {
    position: lerpVec3(previous.position, next.position, t),
    orientation: slerpQuat(previous.orientation, next.orientation, t),
  };
}

/** The renderable part of a sim state. */
export function poseOf(state: SimState): Pose {
  return { position: state.position, orientation: state.orientation };
}
